/**
 * Google sign-in and the ID-token session — T007, FR-AUTH-01/03/04.
 *
 * Three rules from the accepted architecture shape this module:
 *
 *  1. **Google is the only way in.** No password, no other provider, no anonymous mode — none is
 *     offered anywhere in the client, so none can be reached (FR-AUTH-01).
 *  2. **Signing in is not access.** A session here means "Firebase verified this Google identity",
 *     nothing more. Account status and enrollment are decided by the server on every read and write
 *     (FR-AUTH-03), so this module publishes an identity and never a permission.
 *  3. **The admin role is a server decision.** The identity contains no client-derived role hint.
 *     `account.bootstrap` / `me.state` returns the current server decision, and every later operation
 *     re-derives it again from the server allowlist (`BOUNDARIES.md` § 5.2).
 *
 * The session states are modelled as one discriminated union, `unconfigured` included, because a
 * build with no Firebase configuration must render a real screen rather than crash — and must reach
 * no backend while it does.
 */
import {
  GoogleAuthProvider,
  connectAuthEmulator,
  getAuth,
  onIdTokenChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  type Auth,
  type User,
} from 'firebase/auth';
import { normalizeEmail } from '@simple-qa/shared';
import {
  EMULATOR_HOSTS,
  currentEnv,
  firebaseConfigStatus,
  getFirebaseApp,
  emulatorsEnabled,
  type RequiredEnvVar,
} from '../lib/firebase';
import { traceSignInStep } from './signInTrace';

/** The identity a signed-in session carries. Nothing here is an authorization. */
export interface SessionUser {
  readonly uid: string;
  /** Normalized exactly as the server normalizes it, so the two never disagree about an address. */
  readonly email: string;
  /** The Google profile name, or `null`. The only other value the product stores (FR-AUTH-05). */
  readonly displayName: string | null;
  readonly emailVerified: boolean;
}

export type SessionState =
  | { readonly status: 'unconfigured'; readonly missing: readonly RequiredEnvVar[] }
  | { readonly status: 'loading' }
  | { readonly status: 'signedOut' }
  | { readonly status: 'signedIn'; readonly user: SessionUser };

/** What the sign-in button can end up in, beyond a session change. */
export type SignInOutcome = 'signedIn' | 'cancelled' | 'failed';

/** The minimum of `User` this module reads — declared so the mapping can be tested without the SDK. */
export interface AuthUserLike {
  readonly uid: string;
  readonly email: string | null;
  readonly displayName: string | null;
  readonly emailVerified: boolean;
}

/**
 * Firebase `User` → `SessionUser`, or `null` when the identity is unusable.
 *
 * A Google account with no address cannot be matched to an account or an invitation, and the server
 * rejects an unverified address outright (`resolvePrincipal`), so representing either as a signed-in
 * session would put the client and the server in different worlds.
 */
export function toSessionUser(user: AuthUserLike | null): SessionUser | null {
  if (user === null) return null;
  const email = normalizeEmail(user.email ?? '');
  if (email.length === 0 || !user.emailVerified) return null;
  return Object.freeze({
    uid: user.uid,
    email,
    displayName: user.displayName,
    emailVerified: true,
  });
}

/** The initial state of a session before anything has been observed. */
export function initialSessionState(): SessionState {
  const status = firebaseConfigStatus();
  return status.configured ? { status: 'loading' } : { status: 'unconfigured', missing: status.missing };
}

/** A resolved session state for an observed (or absent) Firebase user. */
export function sessionStateFor(user: AuthUserLike | null): SessionState {
  const sessionUser = toSessionUser(user);
  return sessionUser === null ? { status: 'signedOut' } : { status: 'signedIn', user: sessionUser };
}

/**
 * The Firebase error codes that mean "the person changed their mind", not "something broke".
 * They must not raise an error banner: nothing failed, and nothing needs retrying.
 */
export const CANCELLED_SIGN_IN_CODES = Object.freeze([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
] as const);

export function isCancelledSignIn(error: unknown): boolean {
  const code = typeof error === 'object' && error !== null ? (error as { code?: unknown }).code : undefined;
  return typeof code === 'string' && (CANCELLED_SIGN_IN_CODES as readonly string[]).includes(code);
}

/** A failed popup's Firebase code and the start of its message, for the sign-in trace. */
export function describeSignInError(error: unknown): string {
  const value = typeof error === 'object' && error !== null ? (error as { code?: unknown; message?: unknown }) : {};
  const code = typeof value.code === 'string' ? value.code : 'no-code';
  const message = typeof value.message === 'string' ? value.message : String(error);
  return `${code} ${message.slice(0, 120)}`;
}

/* ------------------------------------------------------------------ *
 * The Firebase-backed session
 * ------------------------------------------------------------------ */

let authInstance: Auth | null = null;

function auth(): Auth {
  if (authInstance !== null) return authInstance;
  const instance = getAuth(getFirebaseApp());
  if (emulatorsEnabled(currentEnv())) {
    connectAuthEmulator(instance, EMULATOR_HOSTS.auth, { disableWarnings: true });
  }
  authInstance = instance;
  return instance;
}

/**
 * Observe the session.
 *
 * `onIdTokenChanged`, not `onAuthStateChanged`: it fires on sign-in, on sign-out **and** on every
 * token refresh, which is what keeps a long-lived tab's callables authenticated. The SDK attaches
 * the current token to each callable itself; this subscription exists so the interface knows who is
 * there, not so it can carry a token around.
 */
export function subscribeToSession(onChange: (state: SessionState) => void): () => void {
  return onIdTokenChanged(auth(), (user: User | null) => {
    onChange(sessionStateFor(user));
  });
}

/** FR-AUTH-01. The provider is constructed here and nowhere else, so no second one can appear. */
export async function signInWithGoogle(): Promise<SignInOutcome> {
  traceSignInStep('click');
  try {
    await signInWithPopup(auth(), new GoogleAuthProvider());
    traceSignInStep('popup_ok');
    return 'signedIn';
  } catch (error) {
    traceSignInStep('popup_err', describeSignInError(error));
    return isCancelledSignIn(error) ? 'cancelled' : 'failed';
  }
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(auth());
}
